import React, { useState } from 'react';
import { Form, Input, Modal } from 'antd';

import { SavingsType } from '@totem/components/administration/optimize/savingsTypes/types';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';

type Props = {
  // -1 adds a new savings type; otherwise the index of the one being edited.
  index: number;
  savingsTypes: SavingsType[];
  saving: boolean;
  onClose: () => void;
  onSave: (data: SavingsType[]) => Promise<boolean>;
};

const SavingsTypeDialog = ({
  index,
  savingsTypes,
  saving,
  onClose,
  onSave,
}: Props) => {
  const isNew = index < 0;
  const existing = isNew ? null : savingsTypes[index];
  const [name, setName] = useState<string>(existing?.name ?? '');
  // The value is what tickets store, so it's only set when the type is added.
  const [value, setValue] = useState<string>(existing?.value ?? '');

  const trimmedName = name.trim();
  const trimmedValue = isNew ? value.trim() || trimmedName : existing.value;

  const others = savingsTypes.filter((_, position) => position !== index);
  const nameTaken = others.some(
    (other) => other.name.toLowerCase() === trimmedName.toLowerCase(),
  );
  const valueTaken =
    isNew &&
    others.some(
      (other) => other.value.toLowerCase() === trimmedValue.toLowerCase(),
    );
  const canSave = trimmedName !== '' && !nameTaken && !valueTaken;

  const save = () => {
    const entry = { name: trimmedName, value: trimmedValue };
    const next = isNew
      ? [...savingsTypes, entry]
      : savingsTypes.map((current, position) =>
          position === index ? entry : current,
        );
    onSave(next).then((saved) => {
      if (saved) {
        onClose();
      }
    });
  };

  return (
    <Modal
      open
      title={
        <ModalTitle>
          {isNew ? 'Add savings type' : 'Edit savings type'}
        </ModalTitle>
      }
      okText="Save"
      okButtonProps={{ disabled: !canSave }}
      confirmLoading={saving}
      onOk={save}
      onCancel={onClose}
    >
      <Form layout="vertical">
        <Form.Item
          label="Name"
          required
          validateStatus={nameTaken ? 'error' : undefined}
          help={nameTaken ? 'Another savings type already has this name.' : ''}
        >
          <Input
            autoFocus
            value={name}
            maxLength={100}
            onChange={(event) => setName(event.target.value)}
            onPressEnter={() => canSave && save()}
          />
        </Form.Item>
        <Form.Item
          label="Value"
          validateStatus={valueTaken ? 'error' : undefined}
          help={
            isNew
              ? valueTaken
                ? 'Another savings type already uses this value.'
                : 'Stored on each ticket. Leave blank to use the name. It can’t be changed later.'
              : 'Stored on each ticket, so it can’t be changed. Renaming only changes what users see.'
          }
        >
          <Input
            value={isNew ? value : existing.value}
            placeholder={isNew ? trimmedName : undefined}
            disabled={!isNew}
            maxLength={100}
            onChange={(event) => setValue(event.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SavingsTypeDialog;
